import { useState, useEffect } from 'react';
import { ArticleList } from './components/ArticleList';
import { ArticleReader } from './components/ArticleReader';
import { AuthForm } from './components/AuthForm';
import { InstallPrompt } from './components/InstallPrompt';
import { ExtensionPrompt } from './components/ExtensionPrompt';
import { AuthCallback } from './pages/AuthCallback';
import { PublicArticle } from './pages/PublicArticle';
import { SearchPage } from './pages/SearchPage';
import { SearchBar } from './components/SearchBar';
import { useAuth } from './hooks/useAuth';
import { useArticles, Article } from './hooks/useArticles';
import { useTypography } from './hooks/useTypography';
import { AudioProvider } from './contexts/AudioContext';
import { MiniPlayer } from './components/MiniPlayer';
import './styles/mobile.css';
import './styles/audio.css';

type View = 'list' | 'reader' | 'search' | 'auth-callback';

// Check if this is a public article URL (readl8r.com/s/{token})
function getPublicArticleToken(): string | null {
  const path = window.location.pathname;
  const match = path.match(/^\/s\/([a-zA-Z0-9]+)$/);
  return match ? match[1] : null;
}

export default function App() {
  const { user, loading: authLoading, signUp, signIn, signInWithGoogle, signOut, refreshSession } = useAuth();
  const { articles, loading: articlesLoading, refreshArticles, getArticle, updateReadingProgress } = useArticles(user?.id);

  // Initialize typography CSS variables on mount
  useTypography();

  // Check if we're viewing a public article
  const [publicArticleToken] = useState(() => getPublicArticleToken());

  // Check if we're handling an auth callback
  const [isAuthCallback, setIsAuthCallback] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('token') && !publicArticleToken;
  });

  const [currentView, setCurrentView] = useState<View>('list');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (currentView === 'reader' || currentView === 'search') {
        setCurrentView('list');
        setSelectedArticle(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentView]);

  const handleOpenSearch = () => {
    setCurrentView('search');
    history.pushState({ view: 'search' }, '', '#search');
  };

  const handleBackFromSearch = () => {
    setCurrentView('list');
    history.pushState({ view: 'list' }, '', '#');
  };

  const handleSelectArticle = async (article: Article) => {
    // Fetch full article data if needed
    const fullArticle = await getArticle(article.id);
    if (fullArticle) {
      setSelectedArticle(fullArticle);
      setCurrentView('reader');
      history.pushState({ view: 'reader', articleId: article.id }, '', `#article/${article.id}`);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedArticle(null);
    history.pushState({ view: 'list' }, '', '#');
  };

  const handleProgressUpdate = (progress: number) => {
    if (selectedArticle) {
      updateReadingProgress(selectedArticle.id, progress);
    }
  };

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Service worker registration failed - app will work without offline support
      });
    }
  }, []);

  // Handle public article view (no auth required)
  if (publicArticleToken) {
    return <PublicArticle shareToken={publicArticleToken} />;
  }

  // Handle auth callback from extension
  if (isAuthCallback) {
    return (
      <AuthCallback
        onSuccess={() => {
          setIsAuthCallback(false);
          refreshSession();
        }}
        onError={() => {
          setIsAuthCallback(false);
        }}
      />
    );
  }

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="app loading-screen">
        <div className="loading-spinner-large" />
        <p>Loading...</p>
        <style>{`
          .loading-screen {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            background: var(--bg-primary, #0a0a0a);
            color: var(--text-secondary, #888888);
          }
          .loading-spinner-large {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(249, 115, 22, 0.2);
            border-top-color: var(--accent, #f97316);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show auth form if not logged in
  if (!user) {
    return <AuthForm onSignUp={signUp} onSignIn={signIn} onSignInWithGoogle={signInWithGoogle} />;
  }

  // Get user display info (avatar from Google, or first letter of email)
  const userAvatar = user.user_metadata?.avatar_url || user.user_metadata?.picture;
  const userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0];
  const userInitial = (userName || user.email || 'U')[0].toUpperCase();

  // Main app UI when authenticated
  return (
    <AudioProvider>
      <div className="app">
        <ExtensionPrompt variant="banner" />

      {currentView === 'list' && (
        <>
          <header className="app-header">
            <h1>Readl8r</h1>
            <div className="header-right">
              <SearchBar onClick={handleOpenSearch} compact />
              <span className="article-count">
                {articlesLoading ? '...' : `${articles.length} articles`}
              </span>
              <div className="user-menu">
                <button
                  className="user-avatar-btn"
                  onClick={signOut}
                  title={`${userName}\n${user.email}\nClick to sign out`}
                >
                  {userAvatar ? (
                    <img src={userAvatar} alt={userName} className="user-avatar" />
                  ) : (
                    <span className="user-initial">{userInitial}</span>
                  )}
                </button>
              </div>
            </div>
          </header>
          <main className="app-main">
            {articlesLoading ? (
              <div className="loading-articles">
                <div className="loading-spinner-large" />
                <p>Loading articles...</p>
              </div>
            ) : (
              <ArticleList
                articles={articles}
                onSelectArticle={handleSelectArticle}
                onRefresh={refreshArticles}
              />
            )}
          </main>
        </>
      )}

      {currentView === 'reader' && selectedArticle && (
        <ArticleReader
          article={selectedArticle}
          onBack={handleBackToList}
          onProgressUpdate={handleProgressUpdate}
          userId={user?.id}
        />
      )}

      {currentView === 'search' && (
        <SearchPage
          userId={user?.id}
          onBack={handleBackFromSearch}
        />
      )}

      <InstallPrompt />

      <MiniPlayer />

      <style>{`
        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .user-menu {
          position: relative;
        }
        .user-avatar-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .user-avatar-btn:hover {
          transform: scale(1.05);
        }
        .user-avatar-btn:active {
          transform: scale(0.95);
        }
        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid var(--accent, #f97316);
        }
        .user-initial {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--accent, #f97316);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
        }
        .loading-articles {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          gap: 1rem;
          color: var(--text-secondary, #888888);
        }
        .loading-spinner-large {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(249, 115, 22, 0.2);
          border-top-color: var(--accent, #f97316);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </AudioProvider>
  );
}
